import { useState, useEffect, useCallback } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase/client';

type Theme = 'light' | 'dark';

export function useTheme(userId?: string) {
  // Initialize from localStorage or system preference
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('oilwellx-theme');
      if (saved === 'dark' || saved === 'light') return saved;
      
      // Check system preference
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
    }
    return 'light';
  });

  // Apply theme to document
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('oilwellx-theme', theme);
  }, [theme]);

  // Sync with Firestore when user is logged in
  useEffect(() => {
    if (!userId) return;

    const syncTheme = async () => {
      try {
        // 1. Get preference from Firestore - try tenant-scoped first, then legacy
        // Get companyId from localStorage session
        let companyId: string | undefined;
        try {
          const session = localStorage.getItem('oilwellx_auth_session');
          if (session) {
            const parsed = JSON.parse(session);
            companyId = parsed.companyId;
          }
        } catch (e) { /* ignore */ }
        
        let userRef;
        let userSnap;
        
        if (companyId && companyId !== 'default') {
          userRef = doc(db, 'companies', companyId, 'users', userId);
          userSnap = await getDoc(userRef);
        }
        
        // Fallback to legacy /users
        if (!userSnap?.exists()) {
          userRef = doc(db, 'users', userId);
          userSnap = await getDoc(userRef);
        }
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const remoteTheme = userData.preferences?.theme;
          
          if (remoteTheme && (remoteTheme === 'dark' || remoteTheme === 'light')) {
            // If remote is different from local, update local
            if (remoteTheme !== theme) {
              setTheme(remoteTheme);
            }
          } else {
            // If no remote theme, save current local theme to Firestore
            await updateDoc(userRef, {
              'preferences.theme': theme
            });
          }
        }
      } catch (error) {
        console.error('Error syncing theme:', error);
      }
    };

    syncTheme();
  }, [userId]); // Only run on mount/user change, not every theme change to avoid loops

  // Toggle function
  const toggleTheme = useCallback(async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);

    // Persist to Firestore if logged in
    if (userId) {
      try {
        // Get companyId from localStorage session
        let companyId: string | undefined;
        try {
          const session = localStorage.getItem('oilwellx_auth_session');
          if (session) {
            const parsed = JSON.parse(session);
            companyId = parsed.companyId;
          }
        } catch (e) { /* ignore */ }
        
        let userRef;
        
        if (companyId && companyId !== 'default') {
          const tenantRef = doc(db, 'companies', companyId, 'users', userId);
          const tenantSnap = await getDoc(tenantRef);
          if (tenantSnap.exists()) {
            userRef = tenantRef;
          }
        }
        
        // Fallback to legacy /users
        if (!userRef) {
          userRef = doc(db, 'users', userId);
        }
        
        await updateDoc(userRef, {
          'preferences.theme': newTheme
        });
      } catch (error) {
        console.error('Failed to save theme preference:', error);
      }
    }
  }, [theme, userId]);

  return {
    theme,
    isDarkMode: theme === 'dark',
    toggleTheme,
    setTheme
  };
}
